import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { HttpClient, HttpContext, HttpEventType, HttpHeaders } from '@angular/common/http';
import { WebcamImage, WebcamInitError, WebcamUtil } from 'ngx-webcam';
import { Observable, Subject, interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { QESTION_PAGE_UI } from 'src/app/constants/ui-strings';
import { TestService } from 'src/app/services/test.service';
import { SnapshotResponse } from 'src/app/model/snapshot';
import { SKIP_AUTH_INTERCEPTOR } from 'src/app/constants/constants';

@Component({
  selector: 'app-camera',
  templateUrl: './camera.component.html',
  styleUrl: './camera.component.scss',
})
export class CameraComponent implements OnInit, OnDestroy {
  @Output() pictureTaken = new EventEmitter<WebcamImage>();
  @Output() webCamError = new EventEmitter();

  multipleWebcamsAvailable = false;
  showWebcam = true;
  allowCameraSwitch = true;
  deviceId: string;
  webCamPermissionDenied: boolean;
  errors: WebcamInitError[] = [];
  trigger: Subject<void> = new Subject<void>();
  snapshotInterval: Subscription;
  testService: TestService = inject(TestService);
  http = inject(HttpClient);
  pageUiString = QESTION_PAGE_UI;

  ngOnInit(): void {
    WebcamUtil.getAvailableVideoInputs().then(
      (mediaDevices: MediaDeviceInfo[]) => {
        this.multipleWebcamsAvailable =
          mediaDevices && mediaDevices.length > 1;
      }
    );
    this.snapshotInterval = interval(10000).subscribe(() => {
      this.takeSnapshot();
    });
  }

  ngOnDestroy(): void {
    if (this.snapshotInterval) {
      this.snapshotInterval.unsubscribe();
    }
  }

  handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
    this.webCamPermissionDenied = this.testService.testData()?.proctor_settings
      .enforce_camera
      ? true
      : false;
    this.webCamError.emit();
  }

handleImage(webcamImage: WebcamImage): void {
  this.pictureTaken.emit(webcamImage);

  this.testService.getPreSignedUrl().pipe(
    switchMap((res: SnapshotResponse) => {
      const blob = this.base64ToBlob(webcamImage.imageAsBase64, 'image/png');

      const formData = new FormData();
      const { url, fields } = res.body;

      Object.keys(fields).forEach((key) => {
        formData.append(key, fields[key]);
      });

      formData.append('file', blob);

      return this.http.post(url, formData, {
        responseType: 'text',
        reportProgress: true,
        observe: 'events',
        context: new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true),
      });
    })
  ).subscribe();
  }


  cameraWasSwitched(deviceId: string): void {
    this.deviceId = deviceId;
  }

  takeSnapshot(): void {
    this.trigger.next();
  }

  get invokeObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }
}
